/**
 * Top-level React error boundary for the portal.
 *
 * Catches render-time exceptions anywhere in the tree and renders a recoverable
 * fallback instead of a blank screen. Mounted once in `App.tsx` around the whole
 * Refine application.
 */
import * as React from "react";

import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface the failure in the console for diagnostics; there is no remote
    // error sink in v0.1.
    console.error("[PORTAL_ERROR_BOUNDARY]", error, info.componentStack);
  }

  handleReset(): void {
    this.setState({ error: null });
  }

  override render(): React.ReactNode {
    const { error } = this.state;
    if (error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center text-foreground">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Something went wrong
            </h1>
            <p className="max-w-md text-sm text-muted-foreground">
              {error.message || "An unexpected error occurred while rendering the page."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={this.handleReset}>
              Try again
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                window.location.href = "/";
              }}
            >
              Go home
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
